import { Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { KorviuLoaderComponent } from 'shared';
import { TagComponent } from 'shared/components/tag/tag.component';
import { ProductCardComponent } from 'shared/components/product-card/product-card.component';
import { IProduct } from 'core/types/product.interface';
import { CategoriesService } from 'core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, Subject, switchMap, tap, throttleTime, throwError } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
  selector: 'app-for-you',
  imports: [TagComponent, ProductCardComponent, KorviuLoaderComponent, TranslatePipe],
  templateUrl: './for-you.component.html',
  styleUrl: './for-you.component.scss'
})
export class ForYouComponent implements OnInit {
  readonly categoriesService = inject(CategoriesService);
  readonly destroyRef = inject(DestroyRef);
  readonly router = inject(Router);
  readonly #translateService = inject(TranslateService);

  readonly #limitedTimeOfferFavoriteClick$ = new Subject<IProduct>();

  readonly popularInCommunity = signal<IProduct[]>([]);
  readonly popularInCommunityIsLoading = signal<boolean>(false);

  readonly featured = signal<IProduct[]>([]);
  readonly featuredIsLoading = signal<boolean>(false);

  readonly limitedTimeOffer = signal<IProduct | null>(null);
  readonly limitedTimeOfferIsLoading = signal<boolean>(false);
  readonly currentLang = signal(this.#translateService.currentLang);
  readonly isEnglish = computed(() => this.currentLang() === 'en');

  constructor() {
    this.#limitedTimeOfferFavoriteClick$
      .pipe(
        throttleTime(1000),
        switchMap(product => {
          const previousFavoriteValue = !!product.is_favorite;
          this.#setLimitedTimeOfferFavorite(product.id, !previousFavoriteValue);

          return this.categoriesService.toggleFavoriteProduct(product.id).pipe(
            catchError(error => {
              this.#setLimitedTimeOfferFavorite(product.id, previousFavoriteValue);

              return throwError(() => error);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.#translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.currentLang.set(event.lang));
  }

  ngOnInit(): void {
    this.getPopularInCommunity();
    this.getFeatured();
    this.getLimitedTimeOffers();
  }

  onViewAllTransactionsClick() {
    this.router.navigate(['/', 'workspace', 'balance', 'transactions'], { queryParams: { status: 'redeemed' } });
  }

  onFeaturedViewAllClick(): void {
    void this.router.navigate(['/', 'workspace', 'rewards', 'featured']);
  }

  onPopularInCommunityViewAllClick(): void {
    void this.router.navigate(['/', 'workspace', 'rewards', 'popular-in-community']);
  }

  onLimitedTimeOfferClick(): void {
    const productId = this.limitedTimeOffer()?.id;

    if (!productId) {
      return;
    }

    void this.router.navigate(['workspace', 'rewards', 'product', productId]);
  }

  onLimitedTimeOfferFavoriteClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const product = this.limitedTimeOffer();

    if (!product) {
      return;
    }

    this.#limitedTimeOfferFavoriteClick$.next(product);
  }

  getLimitedTimeOffers() {
    this.limitedTimeOfferIsLoading.set(true);

    this.categoriesService
      .getLimitedTimeOffers({ pageSize: 1, page: 1 })
      .pipe(
        tap(res => {
          this.limitedTimeOffer.set(res.results?.[0]);
        }),
        finalize(() => {
          this.limitedTimeOfferIsLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  getPopularInCommunity() {
    this.popularInCommunityIsLoading.set(true);

    this.categoriesService
      .getPopularInCommunityProducts({ pageSize: 8, page: 1 })
      .pipe(
        tap(res => {
          this.popularInCommunity.set(res.results);
        }),
        finalize(() => {
          this.popularInCommunityIsLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  getFeatured() {
    this.featuredIsLoading.set(true);

    this.categoriesService
      .getFeaturedProducts({ pageSize: 8, page: 1 })
      .pipe(
        tap(res => {
          this.featured.set(res.results);
        }),
        finalize(() => {
          this.featuredIsLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  #setLimitedTimeOfferFavorite(productId: number, isFavorite: boolean): void {
    this.limitedTimeOffer.update(product =>
      product?.id === productId ? { ...product, is_favorite: isFavorite } : product
    );
  }
}
